import ReactApexChart from 'react-apexcharts';
import moment from 'moment';

const FollicleChart = ({ mydata }) => {
	const sortedData = [...(mydata || [])]
		.filter(row => row?.day !== undefined && row?.day !== null && row?.day !== '')
		.sort((a, b) => Number(a.day) - Number(b.day));

	// per-day lookup used to enrich the tooltip with date + totals
	const dayInfo = sortedData.reduce((acc, row) => {
		acc[Number(row.day)] = {
			date: row.createdAt,
			endo: row.endo,
			rtCount: Array.isArray(row.right) ? row.right.length : 0,
			ltCount: Array.isArray(row.left) ? row.left.length : 0,
		};
		return acc;
	}, {});

	const endoPoints = sortedData
		.filter(row => row.endo !== undefined && row.endo !== null && row.endo !== '')
		.map(row => ({ x: Number(row.day), y: Number(row.endo) }));

	// mirrors the paper Deda Hospital Stimulation Monitoring Sheet: Cy/St Day 1-18 across,
	// mm scale 10-28 (step 2) down, with a bottom band for readings under 10mm
	const lastDay = sortedData.length ? Number(sortedData[sortedData.length - 1].day) : 0;
	const maxDay = Math.max(18, lastDay);
	const yMin = 8;
	const yMax = 28;

	// A single endo reading has nothing to connect to, so ApexCharts draws it sloping
	// down to the origin. Duplicate it across the full x-range instead so it renders
	// as a flat horizontal line at that measurement.
	const endoData =
		endoPoints.length === 1
			? [
					{ x: 1, y: endoPoints[0].y, day: endoPoints[0].x },
					{ x: maxDay, y: endoPoints[0].y, day: endoPoints[0].x },
			  ]
			: endoPoints;

	const rightData = sortedData.flatMap(row =>
		(Array.isArray(row.right) ? row.right : []).map(f => ({
			x: Number(row.day),
			y: Number(f.measurement),
			day: Number(row.day),
			totalFollicles: f.totalFollicles,
		}))
	);

	const leftData = sortedData.flatMap(row =>
		(Array.isArray(row.left) ? row.left : []).map(f => ({
			x: Number(row.day),
			y: Number(f.measurement),
			day: Number(row.day),
			totalFollicles: f.totalFollicles,
		}))
	);

	// When both ovaries have a follicle at the same day + measurement, the two scatter
	// markers land on the exact same (x, y) and hide one another. Dodge them apart
	// horizontally (right nudged right, left nudged left) so both stay visible; the
	// true day is kept on `point.day` for the tooltip.
	const DODGE = 0.12;
	const pointKey = p => `${p.day}|${p.y}`;
	const rightKeys = new Set(rightData.map(pointKey));
	leftData.forEach(p => {
		if (rightKeys.has(pointKey(p))) {
			p.x = p.day - DODGE;
		}
	});
	const leftKeys = new Set(leftData.map(pointKey));
	rightData.forEach(p => {
		if (leftKeys.has(pointKey(p))) {
			p.x = p.day + DODGE;
		}
	});

	const series = [
		{ name: 'Endometrium (mm)', type: 'line', data: endoData },
		{ name: 'Right Ovary Follicles', type: 'scatter', data: rightData },
		{ name: 'Left Ovary Follicles', type: 'scatter', data: leftData },
	];

	const options = {
		chart: {
			toolbar: { show: true },
			zoom: { enabled: false },
		},
		colors: ['#00A100', '#2E93fA', '#FEB019'],
		stroke: { width: [3, 0, 0], curve: 'straight' },
		markers: { size: [4, 6, 6], shape: ['circle', 'circle', 'square'], hover: { size: 8 } },
		grid: {
			xaxis: { lines: { show: true } },
			yaxis: { lines: { show: true } },
		},
		xaxis: {
			type: 'numeric',
			title: { text: 'Cy/St Day' },
			min: 1,
			max: maxDay,
			tickAmount: maxDay - 1,
			labels: { formatter: val => Math.round(val) },
		},
		yaxis: {
			title: { text: 'Size (mm)' },
			min: yMin,
			max: yMax,
			tickAmount: (yMax - yMin) / 2,
			labels: { formatter: val => (val <= yMin ? '<10mm Endo' : val) },
		},
		legend: { position: 'top' },
		tooltip: {
			shared: false,
			intersect: true,
			custom: ({ seriesIndex, dataPointIndex, w }) => {
				const point = w.config.series[seriesIndex].data[dataPointIndex];
				const day = point?.day ?? point?.x;
				const info = dayInfo[day] || {};
				return `
					<div class="apexcharts-tooltip-box p-2">
						<div><strong>Day ${day}</strong>${info.date ? ` - ${moment(info.date).format('DD/MM/YYYY')}` : ''}</div>
						<div>Endo: ${info.endo || info.endo === 0 ? `${info.endo}mm` : '--'}</div>
						<div>Right Follicles: ${info.rtCount}</div>
						<div>Left Follicles: ${info.ltCount}</div>
						${point?.totalFollicles !== undefined ? `<div>Follicles @ ${point.y}mm: ${point.totalFollicles}</div>` : ''}
					</div>
				`;
			},
		},
	};

	return (
		<div className="el-chart-w">
			<ReactApexChart options={options} series={series} height="400" type="line" className="apex-charts" />
		</div>
	);
};

export default FollicleChart;
